// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import type { SelectChangeEvent } from '@mui/material/Select';
import {
  CONVERTIBLE_WEIGHT_UNITS,
  CONVERTIBLE_WEIGHT_UNITS_VALUES,
  type ConvertibleWeightUnit,
  getWeightUnitOptions,
  isValidConvertibleWeightUnit,
  isValidWeightUnit,
  upperCaseFirst,
  utils,
} from '@seedplanner/shared-utils';
import Decimal from 'decimal.js';
import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import ToolTipContainer from '../components/common/containers/ToolTipInputContainer';
import UnitsInputContainer from '../components/common/containers/UnitsInputContainer';
import CopyText from '../components/common/copy/CopyText';
import TableView from '../components/common/view/TableView';
import { useLoggedInState } from '../context/LoggedInContext';
import { useUserContext } from '../context/UserContext';
import {
  Box,
  Button,
  Card,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '../lib/base';
import { CenteredView } from '../lib/layout';
import { Link } from '../lib/link';
import * as routes from '../routes';
import { validateNumInput } from '../utils';
import { formatDecimalToCurrency } from '../utils/formatNumbers';

// Ideas for later:
// Hook up to price DB
// Export to CSV/Excel/PDF/Google Sheets
// Add ability to handle multiple crops
// Add ability to save info locally to IndexedDB or localStorage or sessionStorage
// Add amortized equipment expenses
// Add startup expenses- trays, fans, lights, etc.
// Protect against NaN when numbers get too big
// Memoize calculations
// Create short links

export default React.memo(function PriceCalculator() {
  const { preferredWeightUnit } = useUserContext();
  const { isLoggedIn } = useLoggedInState();
  const [params] = useSearchParams();

  React.useEffect(() => {
    const seedCost = params.get(PARAMS.seedCost) || '';
    const seedCostAmt = params.get(PARAMS.seedCostAmt) || '';
    let seedCostUnit = params.get(PARAMS.seedCostUnit) || '';
    if (!isValidConvertibleWeightUnit(seedCostUnit)) {
      seedCostUnit = CONVERTIBLE_WEIGHT_UNITS.POUNDS;
    }
    const sowDensity = params.get(PARAMS.sowDensity) || '';
    // Validate input
    let sowDensityUnit = params.get(PARAMS.sowDensityUnit) || '';
    if (!isValidWeightUnit(sowDensityUnit)) {
      sowDensityUnit = CONVERTIBLE_WEIGHT_UNITS.GRAMS;
    }
    const soilCost = params.get(PARAMS.soilCost) || '';
    const traysPerSoil = params.get(PARAMS.traysPerSoil) || '';
    const packagingPerTray = params.get(PARAMS.packagingPerTray) || '';
    let packagingPerUnit = params.get(PARAMS.packagingPerUnit) || '';
    if (packagingPerUnit !== 'tray' && packagingPerUnit !== 'product') {
      packagingPerUnit = 'tray';
    }
    const otherPerTray = params.get(PARAMS.otherPerTray) || '';
    const yieldPerTray = params.get(PARAMS.yieldPerTray) || '';
    // Validate input
    let yieldUnit = params.get(PARAMS.yieldUnit) || '';
    if (!isValidWeightUnit(yieldUnit)) {
      yieldUnit = CONVERTIBLE_WEIGHT_UNITS.OUNCES;
    }
    const useProductPrice = params.get(PARAMS.useProductPrice) === 'true';
    const grossMargin = params.get(PARAMS.grossMargin) || '';
    const chargePrice = params.get(PARAMS.chargePrice) || '';
    const chargePriceAmt = params.get(PARAMS.chargePriceAmt) || '';
    // Validate input
    let chargePriceUnit = params.get(PARAMS.chargePriceUnit) || '';
    if (!isValidWeightUnit(chargePriceUnit)) {
      chargePriceUnit = CONVERTIBLE_WEIGHT_UNITS.OUNCES;
    }
    const monthlyWater = params.get(PARAMS.monthlyWater) || '';
    const monthlyElectricity = params.get(PARAMS.monthlyElectricity) || '';
    const monthlyLabor = params.get(PARAMS.monthlyLabor) || '';
    const hourlyLabor = params.get(PARAMS.hourlyLabor) || '';
    const laborHoursPerWeek = params.get(PARAMS.laborHoursPerWeek) || '';
    const monthlyRent = params.get(PARAMS.monthlyRent) || '';
    const monthlyPackaging = params.get(PARAMS.monthlyPackaging) || '';
    const monthlyAmortization = params.get(PARAMS.monthlyAmortization) || '';
    const monthlyOther = params.get(PARAMS.monthlyOther) || '';
    // Validate input
    let targetOption = params.get(PARAMS.targetOption) || '';
    if (targetOption !== 'net-profit' && targetOption !== 'tray-count' && targetOption !== 'product-amount') {
      targetOption = 'net-profit';
    }
    const netProfitTarget = params.get(PARAMS.netProfitTarget) || '';
    const trayTarget = params.get(PARAMS.trayTarget) || '';
    const productTarget = params.get(PARAMS.productTarget) || '';
    const taxRate = params.get(PARAMS.taxRate) || '';
    const trayBuff = params.get(PARAMS.trayBuff) || '';

    setSeedCost(seedCost);
    setSeedCostPerAmount(seedCostAmt);
    setSeedCostPerAmountUnit(seedCostUnit as ConvertibleWeightUnit);
    setSowDensity(sowDensity);
    setSowDensityUnit(sowDensityUnit as ConvertibleWeightUnit);
    setSoilCost(soilCost);
    setTraysPerSoilAmount(traysPerSoil);
    setPackagingLabelingCostPerTray(packagingPerTray);
    setPackagingLabelingCostPerTrayUnit(packagingPerUnit as 'tray' | 'product');
    setOtherCostPerTray(otherPerTray);
    setYieldPerTray(yieldPerTray);
    setYieldUnit(yieldUnit as ConvertibleWeightUnit);
    setUseProductPrice(useProductPrice);
    setTargetGrossMarginPercentage(grossMargin);
    setChargePrice(chargePrice);
    setChargePricePerAmount(chargePriceAmt);
    setChargePricePerAmountUnit(chargePriceUnit as ConvertibleWeightUnit);
    setMonthlyWaterCost(monthlyWater);
    setMonthlyElectricityCost(monthlyElectricity);
    setMonthlyLaborCost(monthlyLabor);
    setHourlyLaborRate(hourlyLabor);
    setLaborHoursPerWeek(laborHoursPerWeek);
    setMonthlyRentCost(monthlyRent);
    setMonthlyPackagingCost(monthlyPackaging);
    setMonthlyAmortizedEquipmentCost(monthlyAmortization);
    setMonthlyOtherCost(monthlyOther);
    setProfitInputType(targetOption as 'net-profit' | 'tray-count' | 'product-amount');
    setNetProfitAmount(netProfitTarget);
    setTraysSoldAmount(trayTarget);
    setProductSalesAmount(productTarget);
    setTaxRate(taxRate);
    setTrayBuffer(trayBuff);
  }, [params]);

  // For example: $10 per 4 lb
  const [seedCost, setSeedCost] = React.useState(''); // $10
  const [seedCostPerAmount, setSeedCostPerAmount] = React.useState('1'); // 4
  const [seedCostPerAmountUnit, setSeedCostPerAmountUnit] = React.useState<ConvertibleWeightUnit>(
    CONVERTIBLE_WEIGHT_UNITS.POUNDS,
  ); // lb

  const [sowDensity, setSowDensity] = React.useState('');
  const [sowDensityUnit, setSowDensityUnit] = React.useState<ConvertibleWeightUnit>(preferredWeightUnit);

  const [yieldPerTray, setYieldPerTray] = React.useState('');
  const [yieldUnit, setYieldUnit] = React.useState<ConvertibleWeightUnit>(preferredWeightUnit);

  const [otherCostPerTray, setOtherCostPerTray] = React.useState('');
  const [packagingLabelingCostPerTray, setPackagingLabelingCostPerTray] = React.useState('');
  const [packagingLabelingCostPerTrayUnit, setPackagingLabelingCostPerTrayUnit] = React.useState<'tray' | 'product'>(
    'tray',
  );
  const [trayBuffer, setTrayBuffer] = React.useState('');

  const [trayBufferValidation, setTrayBufferValidation] = React.useState('');
  const [yieldPerTrayValidation, setYieldPerTrayValidation] = React.useState('');
  const [sowDensityValidation, setSowDensityValidation] = React.useState('');
  const [seedCostValidation, setSeedCostValidation] = React.useState('');
  const [chargePriceValidation, setChargePriceValidation] = React.useState('');
  const [chargePricePerAmountValidation, setChargePricePerAmountValidation] = React.useState('');
  const [seedCostPerAmountValidation, setSeedCostPerAmountValidation] = React.useState('');
  const [otherCostPerTrayValidation, setOtherCostPerTrayValidation] = React.useState('');
  const [targetGrossMarginValidation, setTargetGrossMarginValidation] = React.useState('');
  const [packagingLabelingCostPerTrayValidation, setPackagingLabelingCostPerTrayValidation] = React.useState('');

  const [soilCost, setSoilCost] = React.useState('');
  const [soilCostValidation, setSoilCostValidation] = React.useState('');
  const [traysPerSoilAmount, setTraysPerSoilAmount] = React.useState('');
  const [traysPerSoilAmountValidation, setTraysPerSoilAmountValidation] = React.useState('');

  // Use product price or use desired margin percentage
  const [useProductPrice, setUseProductPrice] = React.useState(true);

  // The amount the user is charging for their final product
  // For example: $10 per 1 ounce
  const [chargePrice, setChargePrice] = React.useState(''); // $10
  const [chargePricePerAmount, setChargePricePerAmount] = React.useState('1'); // 1
  const [chargePricePerAmountUnit, setChargePricePerAmountUnit] =
    React.useState<ConvertibleWeightUnit>(preferredWeightUnit); // ounce

  const [targetGrossMarginPercentage, setTargetGrossMarginPercentage] = React.useState('');

  const onChangeTrayBuffer = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setTrayBufferValidation(msg),
      validateGTE: 0,
    });
    setTrayBuffer(e.target.value);
  }, []);
  const onTrayBufferBlur = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setTrayBufferValidation(msg),
      validateGTE: 0,
    });
  }, []);
  const onChangePackagingLabelingCostPerTrayUnit = React.useCallback((event: SelectChangeEvent<string>) => {
    setPackagingLabelingCostPerTrayUnit(event.target.value as 'tray' | 'product');
  }, []);
  const onChangeSoilCost = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setSoilCostValidation(msg),
      validateGT: 0,
    });
    setSoilCost(e.target.value);
  }, []);
  const onSoilCostBlur = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setSoilCostValidation(msg),
      validateGT: 0,
    });
  }, []);
  const onChangeTraysPerSoilAmount = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      validateNumInput({
        event: e,
        setValidationMessage: (msg) => setTraysPerSoilAmountValidation(msg),
        validateGT: 0,
      });
      setTraysPerSoilAmount(e.target.value);
    },
    [],
  );
  const onTraysPerSoilAmountBlur = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setTraysPerSoilAmountValidation(msg),
      validateGT: 0,
    });
  }, []);
  const onChangePackagingLabelingCostPerTray = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      validateNumInput({
        event: e,
        setValidationMessage: (msg) => setPackagingLabelingCostPerTrayValidation(msg),
        validateGTE: 0,
      });
      setPackagingLabelingCostPerTray(e.target.value);
    },
    [],
  );
  const onPackagingLabelingCostPerTrayBlur = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      validateNumInput({
        event: e,
        setValidationMessage: (msg) => setPackagingLabelingCostPerTrayValidation(msg),
        validateGTE: 0,
      });
    },
    [],
  );
  const onChangeUseProductPrice = React.useCallback((e: React.MouseEvent<HTMLElement>, newValue: boolean | null) => {
    if (newValue == null) {
      return;
    }
    setUseProductPrice(newValue);
  }, []);
  const onChangeTargetGrossMarginPercentage = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      validateNumInput({
        event: e,
        setValidationMessage: (msg) => setTargetGrossMarginValidation(msg),
        validateGTE: 0,
        validateLT: 100,
      });
      setTargetGrossMarginPercentage(e.target.value);
    },
    [],
  );
  const onTargetGrossMarginPercentageBlur = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      validateNumInput({
        event: e,
        setValidationMessage: (msg) => setTargetGrossMarginValidation(msg),
        validateGTE: 0,
        validateLT: 100,
      });
    },
    [],
  );
  const onChangeOtherCostPerTray = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setOtherCostPerTrayValidation(msg),
      validateGT: 0,
    });
    setOtherCostPerTray(e.target.value);
  }, []);
  const onOtherCostPerTrayBlur = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setOtherCostPerTrayValidation(msg),
      validateGT: 0,
    });
  }, []);
  const onChangeSeedCost = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setSeedCostValidation(msg),
      validateGT: 0,
    });
    setSeedCost(e.target.value);
  }, []);
  const onSeedCostBlur = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setSeedCostValidation(msg),
      validateGT: 0,
    });
  }, []);
  const onChangeSeedCostPerAmount = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      validateNumInput({
        event: e,
        setValidationMessage: (msg) => setSeedCostPerAmountValidation(msg),
        validateGT: 0,
      });
      setSeedCostPerAmount(e.target.value);
    },
    [],
  );
  const onSeedCostPerAmountBlur = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setSeedCostPerAmountValidation(msg),
      validateGT: 0,
    });
  }, []);
  const onChangeSeedCostPerAmountUnit = React.useCallback((event: SelectChangeEvent<string>) => {
    setSeedCostPerAmountUnit(event.target.value as ConvertibleWeightUnit);
  }, []);
  const onChangeChargePrice = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setChargePriceValidation(msg),
      validateGT: 0,
    });
    setChargePrice(e.target.value);
  }, []);
  const onChargePriceBlur = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setChargePriceValidation(msg),
      validateGT: 0,
    });
  }, []);
  const onChangeChargePricePerAmount = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      validateNumInput({
        event: e,
        setValidationMessage: (msg) => setChargePricePerAmountValidation(msg),
        validateGT: 0,
      });
      setChargePricePerAmount(e.target.value);
    },
    [],
  );
  const onChargePricePerAmountBlur = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      validateNumInput({
        event: e,
        setValidationMessage: (msg) => setChargePricePerAmountValidation(msg),
        validateGT: 0,
      });
    },
    [],
  );
  const onChangeChargePricePerAmountUnit = React.useCallback((event: SelectChangeEvent<string>) => {
    setChargePricePerAmountUnit(event.target.value as ConvertibleWeightUnit);
  }, []);
  const onChangeYieldPerTray = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setYieldPerTrayValidation(msg),
      validateGT: 0,
    });
    setYieldPerTray(e.target.value);
  }, []);
  const onYieldPerTrayBlur = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setYieldPerTrayValidation(msg),
      validateGT: 0,
    });
  }, []);
  const onYieldUnitChange = React.useCallback((event: SelectChangeEvent<string>) => {
    setYieldUnit(event.target.value as ConvertibleWeightUnit);
  }, []);
  const onSowDensityUnitChange = React.useCallback((event: SelectChangeEvent<string>) => {
    setSowDensityUnit(event.target.value as ConvertibleWeightUnit);
  }, []);
  const onChangeSowDensity = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSowDensity(e.target.value);
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setSowDensityValidation(msg),
      validateGT: 0,
    });
  }, []);
  const onSowDensityBlur = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setSowDensityValidation(msg),
      validateGT: 0,
    });
  }, []);

  interface CostsAndMargins {
    readonly costPerTrayTotal: string;
    readonly costPerTrayTotalDecimal: Decimal;
    readonly costPerTraySeed: string;
    readonly costPerTraySoil: string;
    readonly costPerTrayPackaging: string;
    readonly costPerTrayOther: string;
    readonly costPerSellingAmountWithBuffer: string;
    readonly costPerSellingAmountWithBufferDecimal: Decimal;
    readonly trayBufferPercentageDecimal: Decimal;
    readonly traysNeededForSellingAmountWithoutBufferDecimal: Decimal;
    readonly traysNeededForSellingAmountWithBuffer: string;
    readonly traysNeededForSellingAmountWithBufferDecimal: Decimal;
    readonly marginPerSellingAmount: string;
    readonly marginPerSellingAmountDecimal: Decimal;
    readonly marginPerTray: string;
    readonly marginPercentage: string;
    readonly sellingPricePerSellingAmount: string;
    readonly sellingPricePerSellingAmountDecimal: Decimal;
    readonly sellingPricePerTrayDecimal: Decimal;
    readonly sellingPricePerTray: string;
  }
  const getCostsAndMargins = React.useCallback((): CostsAndMargins => {
    const yieldPerTrayNum = Number.parseFloat(yieldPerTray) || 0;
    const seedCostNum = Number.parseFloat(seedCost) || 0;
    const seedCostPerAmountNum = Number.parseFloat(seedCostPerAmount) || 0;
    const sowDensityNum = Number.parseFloat(sowDensity) || 0;
    const chargePriceNum = Number.parseFloat(chargePrice) || 0;
    const sellingAmountNum = Number.parseFloat(chargePricePerAmount) || 0;
    const targetGrossMarginNum = Number.parseFloat(targetGrossMarginPercentage) || 0;
    const traysPerSoilAmountNum = new Decimal(Number.parseFloat(traysPerSoilAmount) || 0);
    const soilCostNum = new Decimal(Number.parseFloat(soilCost) || 0);
    const otherCostPerTrayNum = new Decimal(Number.parseFloat(otherCostPerTray) || 0);
    const packagingLabelingCostPerTrayNum = new Decimal(Number.parseFloat(packagingLabelingCostPerTray) || 0);
    const trayBufferPercentageNum = new Decimal(Number.parseFloat(trayBuffer) || 0).div(100);

    // Yield is needed to calculate number of trays and margins
    // If there is no valid yield then the following should be blank or 0:
    // Trays Needed For X ounces/grams
    // Gross Margin
    // Margin Per Tray
    const yieldIsValid = yieldPerTrayNum > 0;
    // 4lb -> X grams
    const seedAmountInGrams = utils.convert({
      inputUnit: seedCostPerAmountUnit,
      outputUnit: CONVERTIBLE_WEIGHT_UNITS.GRAMS,
      quantity: seedCostPerAmountNum,
    });
    // $ per gram
    const costPerGram = Decimal.div(seedCostNum, seedAmountInGrams);
    // grams per tray
    const sowDensityInGrams = utils.convert({
      inputUnit: sowDensityUnit,
      outputUnit: CONVERTIBLE_WEIGHT_UNITS.GRAMS,
      quantity: sowDensityNum,
    });
    const costPerTraySeed = sowDensityInGrams <= 0 ? new Decimal(0) : costPerGram.mul(sowDensityInGrams);
    const soilCostPerTray =
      traysPerSoilAmountNum.isZero() || traysPerSoilAmountNum.lessThan(0)
        ? new Decimal(0)
        : soilCostNum.div(traysPerSoilAmountNum);

    const yieldPerTrayGrams = utils.convert({
      inputUnit: yieldUnit,
      outputUnit: CONVERTIBLE_WEIGHT_UNITS.GRAMS,
      quantity: yieldPerTrayNum,
    });
    const sellingAmountGrams = utils.convert({
      inputUnit: chargePricePerAmountUnit,
      outputUnit: CONVERTIBLE_WEIGHT_UNITS.GRAMS,
      quantity: sellingAmountNum,
    });

    const traysNeededForSellingAmountWithoutBuffer = yieldIsValid
      ? Decimal.div(sellingAmountGrams, yieldPerTrayGrams)
      : new Decimal(0);
    const bufferTrays = traysNeededForSellingAmountWithoutBuffer.mul(trayBufferPercentageNum);
    const traysNeededForSellingAmountWithBuffer = traysNeededForSellingAmountWithoutBuffer.add(bufferTrays);

    const packagingCostPerTray =
      packagingLabelingCostPerTrayUnit === 'tray'
        ? packagingLabelingCostPerTrayNum
        : // We need to know trays per product to calculate packaging cost
          traysNeededForSellingAmountWithoutBuffer.isZero() ||
            traysNeededForSellingAmountWithoutBuffer.lessThan(0) ||
            packagingLabelingCostPerTrayNum.isZero()
          ? new Decimal(0)
          : packagingLabelingCostPerTrayNum.div(traysNeededForSellingAmountWithoutBuffer);
    const costPerTrayTotal = [costPerTraySeed, soilCostPerTray, packagingCostPerTray, otherCostPerTrayNum].reduce(
      (acc, val) => acc.add(val),
      new Decimal(0),
    );
    const costPerSellingAmountWithoutBuffer = Decimal.mul(traysNeededForSellingAmountWithoutBuffer, costPerTrayTotal);
    const costPerSellingAmountWithBuffer = Decimal.mul(traysNeededForSellingAmountWithBuffer, costPerTrayTotal);

    const useChargePrice = () => {
      const sellingPricePerSellingAmount = new Decimal(chargePriceNum);
      const sellingPricePerTray = sellingPricePerSellingAmount.div(traysNeededForSellingAmountWithoutBuffer);
      const marginPerSellingAmount = Decimal.sub(chargePriceNum, costPerSellingAmountWithBuffer);
      const marginPerTray = sellingPricePerTray.sub(costPerTrayTotal);
      const marginPercentage = Decimal.div(marginPerSellingAmount, chargePriceNum).mul(100);

      return {
        marginPerSellingAmount,
        marginPerTray,
        marginPercentage,
        sellingPricePerSellingAmount,
        sellingPricePerTray,
      };
    };

    const useGrossMargin = () => {
      const marginPercentage = new Decimal(targetGrossMarginNum);
      const sellingPricePerSellingAmount = Decimal.div(
        costPerSellingAmountWithoutBuffer,
        Decimal.sub(1, Decimal.div(targetGrossMarginNum, 100)),
      );
      const sellingPricePerTray = sellingPricePerSellingAmount.div(traysNeededForSellingAmountWithoutBuffer);
      const marginPerSellingAmount = Decimal.sub(sellingPricePerSellingAmount, costPerSellingAmountWithBuffer);
      const marginPerTray = sellingPricePerTray.sub(costPerTrayTotal);

      return {
        marginPerSellingAmount,
        marginPerTray,
        marginPercentage,
        sellingPricePerSellingAmount,
        sellingPricePerTray,
      };
    };

    const {
      marginPerSellingAmount,
      sellingPricePerTray,
      marginPerTray,
      marginPercentage,
      sellingPricePerSellingAmount,
    } = useProductPrice ? useChargePrice() : useGrossMargin();

    return {
      costPerTrayTotal: formatDecimalToCurrency(costPerTrayTotal),
      costPerTrayTotalDecimal: costPerTrayTotal,
      costPerTraySeed: formatDecimalToCurrency(costPerTraySeed),
      costPerTrayOther: formatDecimalToCurrency(otherCostPerTrayNum),
      costPerTrayPackaging: formatDecimalToCurrency(packagingCostPerTray),
      costPerTraySoil: formatDecimalToCurrency(soilCostPerTray),
      costPerSellingAmountWithBuffer: formatDecimalToCurrency(costPerSellingAmountWithBuffer),
      costPerSellingAmountWithBufferDecimal: costPerSellingAmountWithBuffer,
      trayBufferPercentageDecimal: trayBufferPercentageNum,
      traysNeededForSellingAmountWithoutBufferDecimal: traysNeededForSellingAmountWithoutBuffer,
      traysNeededForSellingAmountWithBufferDecimal: traysNeededForSellingAmountWithBuffer,
      // Not currency but works for now anyway
      traysNeededForSellingAmountWithBuffer: formatDecimalToCurrency(traysNeededForSellingAmountWithBuffer),
      marginPerSellingAmount: formatDecimalToCurrency(marginPerSellingAmount),
      marginPerSellingAmountDecimal: marginPerSellingAmount,
      marginPerTray: formatDecimalToCurrency(marginPerTray),
      marginPercentage: marginPercentage.isNaN() || marginPercentage.lessThan(0) ? '' : marginPercentage.toFixed(0),
      sellingPricePerSellingAmount: formatDecimalToCurrency(sellingPricePerSellingAmount),
      sellingPricePerSellingAmountDecimal: sellingPricePerSellingAmount,
      sellingPricePerTrayDecimal: sellingPricePerTray,
      sellingPricePerTray:
        sellingPricePerTray.isNaN() || sellingPricePerTray.lessThan(0)
          ? ''
          : formatDecimalToCurrency(sellingPricePerTray),
    };
  }, [
    yieldPerTray,
    seedCost,
    seedCostPerAmount,
    sowDensity,
    chargePrice,
    chargePricePerAmount,
    targetGrossMarginPercentage,
    seedCostPerAmountUnit,
    sowDensityUnit,
    chargePricePerAmountUnit,
    yieldUnit,
    otherCostPerTray,
    packagingLabelingCostPerTray,
    soilCost,
    traysPerSoilAmount,
    useProductPrice,
    packagingLabelingCostPerTrayUnit,
    trayBuffer,
  ]);

  const {
    costPerTrayTotal,
    costPerTrayTotalDecimal,
    costPerTrayOther,
    costPerTraySeed,
    costPerTraySoil,
    costPerTrayPackaging,
    costPerSellingAmountWithBuffer,
    costPerSellingAmountWithBufferDecimal,
    trayBufferPercentageDecimal,
    traysNeededForSellingAmountWithoutBufferDecimal,
    traysNeededForSellingAmountWithBuffer,
    marginPerSellingAmount,
    marginPerSellingAmountDecimal,
    marginPerTray,
    marginPercentage,
    sellingPricePerSellingAmount,
    sellingPricePerSellingAmountDecimal,
    sellingPricePerTrayDecimal,
    sellingPricePerTray,
  } = React.useMemo(() => getCostsAndMargins(), [getCostsAndMargins]);

  // const disableSave =
  //   desiredAmount === '' || yieldPerTray === '' || yieldPerTrayValidation !== '' || desiredAmountValidation !== '';

  const form = React.useMemo(
    () => (
      <Box display="flex" flexDirection="column" sx={{ m: 1 }} gap={2}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 12, md: 'grow' }}>
            <TextField
              aria-describedby="seed-cost"
              id="seed-cost"
              label={`Seed Cost ($ per ${seedCostPerAmount || '0'} ${seedCostPerAmountUnit})`}
              value={seedCost}
              onChange={onChangeSeedCost}
              onBlur={onSeedCostBlur}
              subtext={seedCostValidation}
              error={seedCostValidation !== ''}
              type="number"
              inputMode="decimal"
              numberMin={0}
              hasSubtext
            />
          </Grid>
          <Grid container size={{ xs: 12, sm: 12, md: 'auto' }} spacing={2}>
            <Grid size={{ xs: 12, sm: 'grow' }}>
              <TextField
                aria-describedby="seed-cost-per-amount"
                id="seed-cost-per-amount"
                label="Seed Amount"
                value={seedCostPerAmount}
                onChange={onChangeSeedCostPerAmount}
                onBlur={onSeedCostPerAmountBlur}
                subtext={seedCostPerAmountValidation}
                error={seedCostPerAmountValidation !== ''}
                type="number"
                inputMode="decimal"
                numberMin={0}
                hasSubtext
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 'auto' }}>
              <FormControl fullWidth>
                <InputLabel id="seed-cost-per-amount-unit-selector">Units</InputLabel>
                <Select
                  aria-describedby="seed-cost-per-amount-unit-selector"
                  id="seed-cost-per-amount-unit-selector"
                  labelId="seed-cost-per-amount-unit-selector"
                  label="Units"
                  value={seedCostPerAmountUnit}
                  onChange={onChangeSeedCostPerAmountUnit}
                  required={true}
                >
                  {CONVERTIBLE_WEIGHT_UNITS_VALUES.map((sizeUnit) => {
                    return (
                      <MenuItem key={sizeUnit} value={sizeUnit}>
                        {sizeUnit}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
        <UnitsInputContainer
          input={
            <TextField
              aria-describedby="sow-density"
              id="sow-density"
              label={`Sow Density (${sowDensityUnit} per tray)`}
              value={sowDensity}
              onChange={onChangeSowDensity}
              onBlur={onSowDensityBlur}
              subtext={sowDensityValidation}
              error={sowDensityValidation !== ''}
              type="number"
              inputMode="decimal"
              numberMin={0}
              hasSubtext
            />
          }
          units={
            <FormControl fullWidth>
              <InputLabel id="units-selector">Units</InputLabel>
              <Select
                aria-describedby="units-selector"
                id="units-selector"
                labelId="units-selector"
                label="Units"
                value={sowDensityUnit}
                onChange={onSowDensityUnitChange}
                required={true}
              >
                {getWeightUnitOptions(preferredWeightUnit).map((sizeUnit) => {
                  return (
                    <MenuItem key={sizeUnit} value={sizeUnit}>
                      {sizeUnit}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          }
        />
        <TextField
          aria-describedby="soil-cost"
          id="soil-cost"
          label="Soil Cost ($ per bag)"
          value={soilCost}
          onChange={onChangeSoilCost}
          onBlur={onSoilCostBlur}
          subtext={soilCostValidation}
          error={soilCostValidation !== ''}
          type="number"
          inputMode="decimal"
          numberMin={0}
          hasSubtext
        />
        <TextField
          aria-describedby="trays-per-soil-amount"
          id="trays-per-soil-amount"
          label="Trays Per Bag of Soil"
          value={traysPerSoilAmount}
          onChange={onChangeTraysPerSoilAmount}
          onBlur={onTraysPerSoilAmountBlur}
          subtext={traysPerSoilAmountValidation}
          error={traysPerSoilAmountValidation !== ''}
          type="number"
          inputMode="decimal"
          numberMin={0}
          hasSubtext
        />
        <UnitsInputContainer
          input={
            <TextField
              aria-describedby="packaging-labeling-cost-per-tray"
              id="packaging-labeling-cost-per-tray"
              label={`Packaging and Labeling Cost Per ${packagingLabelingCostPerTrayUnit === 'tray' ? 'Tray' : 'Product'} ($ per ${packagingLabelingCostPerTrayUnit})`}
              value={packagingLabelingCostPerTray}
              onChange={onChangePackagingLabelingCostPerTray}
              onBlur={onPackagingLabelingCostPerTrayBlur}
              subtext={packagingLabelingCostPerTrayValidation}
              error={packagingLabelingCostPerTrayValidation !== ''}
              type="number"
              inputMode="decimal"
              numberMin={0}
              hasSubtext
            />
          }
          units={
            <FormControl fullWidth>
              <InputLabel id="packaging-labeling-cost-per-tray-units-selector">Units</InputLabel>
              <Select
                aria-describedby="packaging-labeling-cost-per-tray-units-selector"
                id="packaging-labeling-cost-per-tray-units-selector"
                labelId="packaging-labeling-cost-per-tray-units-selector"
                label="Units"
                value={packagingLabelingCostPerTrayUnit}
                onChange={onChangePackagingLabelingCostPerTrayUnit}
                required={true}
              >
                {['tray', 'product'].map((sizeUnit) => {
                  return (
                    <MenuItem key={sizeUnit} value={sizeUnit}>
                      {sizeUnit}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          }
        />
        <ToolTipContainer message="Use this to account for the cost of lost trays. If you typically lose 1% of your trays, then enter 1% here and the calculator will add 1% to the trays needed to grow.">
          <TextField
            aria-describedby="safety-margin"
            id="safety-margin"
            label="Safety Margin (%)"
            value={trayBuffer}
            onChange={onChangeTrayBuffer}
            onBlur={onTrayBufferBlur}
            subtext={trayBufferValidation}
            error={trayBufferValidation !== ''}
            type="number"
            inputMode="decimal"
            numberMin={0}
            numberMax={100}
            hasSubtext
          />
        </ToolTipContainer>
        <TextField
          aria-describedby="other-cost-per-tray"
          id="other-cost-per-tray"
          label="Other Cost Per Tray (Ex: disposal fees)"
          value={otherCostPerTray}
          onChange={onChangeOtherCostPerTray}
          onBlur={onOtherCostPerTrayBlur}
          subtext={otherCostPerTrayValidation}
          error={otherCostPerTrayValidation !== ''}
          type="number"
          inputMode="decimal"
          numberMin={0}
          hasSubtext
        />
        <UnitsInputContainer
          input={
            <TextField
              aria-describedby="expected-yield"
              id="expected-yield"
              label={`Crop Yield (${yieldUnit} per tray)`}
              value={yieldPerTray}
              onChange={onChangeYieldPerTray}
              onBlur={onYieldPerTrayBlur}
              subtext={yieldPerTrayValidation}
              error={yieldPerTrayValidation !== ''}
              type="number"
              inputMode="decimal"
              numberMin={0}
              hasSubtext
              required
            />
          }
          units={
            <FormControl fullWidth>
              <InputLabel id="yield-units-selector">Units</InputLabel>
              <Select
                aria-describedby="yield-units-selector"
                id="yield-units-selector"
                labelId="yield-units-selector"
                label="Units"
                value={yieldUnit}
                onChange={onYieldUnitChange}
                required={true}
              >
                {getWeightUnitOptions(preferredWeightUnit).map((sizeUnit) => {
                  return (
                    <MenuItem key={sizeUnit} value={sizeUnit}>
                      {sizeUnit}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          }
        />
        <Grid container alignItems="center" justifyContent="flex-start" spacing={1}>
          <Grid>
            <Typography variant="body1">Enter One:</Typography>
          </Grid>
          <Grid>
            <ToggleButtonGroup
              size="small"
              value={useProductPrice}
              exclusive
              onChange={onChangeUseProductPrice}
              aria-label="select-toggle-date"
            >
              <ToggleButton value={false} aria-label="enter margin percentage">
                Target Gross Margin
              </ToggleButton>
              <ToggleButton value={true} aria-label="enter selling price">
                Target Selling Price
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          {useProductPrice ? (
            <Grid size={{ xs: 12, sm: 12, md: 'grow' }}>
              <TextField
                aria-describedby="charge-price"
                id="charge-price"
                label={`Container Price ($ per ${chargePricePerAmount || '0'} ${chargePricePerAmountUnit})`}
                value={chargePrice}
                onChange={onChangeChargePrice}
                onBlur={onChargePriceBlur}
                subtext={chargePriceValidation}
                error={chargePriceValidation !== ''}
                type="number"
                inputMode="decimal"
                numberMin={0}
                hasSubtext
                required
              />
            </Grid>
          ) : (
            <Grid size={{ xs: 12, sm: 12, md: 'grow' }}>
              <TextField
                aria-describedby="target-gross-margin"
                id="target-gross-margin"
                label="Target Gross Margin %"
                value={targetGrossMarginPercentage}
                onChange={onChangeTargetGrossMarginPercentage}
                onBlur={onTargetGrossMarginPercentageBlur}
                subtext={targetGrossMarginValidation}
                error={targetGrossMarginValidation !== ''}
                type="number"
                inputMode="decimal"
                numberMin={0}
                numberMax={99}
                hasSubtext
                required
              />
            </Grid>
          )}
          <Grid container size={{ xs: 12, sm: 12, md: 'auto' }} spacing={2}>
            <Grid size={{ xs: 12, sm: 'grow' }}>
              <TextField
                aria-describedby="charge-price-per-amount"
                id="charge-price-per-amount"
                label="Container Size"
                value={chargePricePerAmount}
                onChange={onChangeChargePricePerAmount}
                onBlur={onChargePricePerAmountBlur}
                subtext={chargePricePerAmountValidation}
                error={chargePricePerAmountValidation !== ''}
                type="number"
                inputMode="decimal"
                numberMin={0}
                hasSubtext
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 'auto' }}>
              <FormControl fullWidth>
                <InputLabel id="charge-price-amoount-unit-selector">Units</InputLabel>
                <Select
                  aria-describedby="charge-price-amoount-unit-selector"
                  id="charge-price-amoount-unit-selector"
                  labelId="charge-price-amoount-unit-selector"
                  label="Units"
                  value={chargePricePerAmountUnit}
                  onChange={onChangeChargePricePerAmountUnit}
                  required={true}
                >
                  {getWeightUnitOptions(preferredWeightUnit).map((sizeUnit) => {
                    return (
                      <MenuItem key={sizeUnit} value={sizeUnit}>
                        {sizeUnit}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    ),
    [
      seedCost,
      seedCostPerAmount,
      seedCostPerAmountUnit,
      sowDensity,
      sowDensityUnit,
      soilCost,
      traysPerSoilAmount,
      packagingLabelingCostPerTray,
      packagingLabelingCostPerTrayUnit,
      otherCostPerTray,
      yieldPerTray,
      yieldUnit,
      useProductPrice,
      targetGrossMarginPercentage,
      chargePrice,
      chargePricePerAmount,
      chargePricePerAmountUnit,
      trayBuffer,
      chargePricePerAmountValidation,
      yieldPerTrayValidation,
      traysPerSoilAmountValidation,
      trayBufferValidation,
      targetGrossMarginValidation,
      sowDensityValidation,
      soilCostValidation,
      seedCostValidation,
      seedCostPerAmountValidation,
      preferredWeightUnit,
      packagingLabelingCostPerTrayValidation,
      otherCostPerTrayValidation,
      chargePriceValidation,
      onChangeChargePrice,
      onChangeChargePricePerAmountUnit,
      onChangeSeedCost,
      onChangeUseProductPrice,
      onYieldUnitChange,
      onYieldPerTrayBlur,
      onTraysPerSoilAmountBlur,
      onTrayBufferBlur,
      onTargetGrossMarginPercentageBlur,
      onSowDensityUnitChange,
      onSowDensityBlur,
      onSoilCostBlur,
      onSeedCostPerAmountBlur,
      onSeedCostBlur,
      onPackagingLabelingCostPerTrayBlur,
      onOtherCostPerTrayBlur,
      onChargePricePerAmountBlur,
      onChargePriceBlur,
      onChangeTraysPerSoilAmount,
      onChangeYieldPerTray,
      onChangeTrayBuffer,
      onChangeTargetGrossMarginPercentage,
      onChangeSowDensity,
      onChangeSoilCost,
      onChangeSeedCostPerAmountUnit,
      onChangePackagingLabelingCostPerTrayUnit,
      onChangeSeedCostPerAmount,
      onChangePackagingLabelingCostPerTray,
      onChangeChargePricePerAmount,
      onChangeOtherCostPerTray,
    ],
  );

  const columns = React.useMemo(
    (): Array<[string, React.ReactElement | string] | [string, React.ReactElement | string, number]> => [
      ['Seed Cost Per Tray', `$${costPerTraySeed}`],
      ['Soil Cost Per Tray', `$${costPerTraySoil}`],
      ['Packaging Cost Per Tray', `$${costPerTrayPackaging}`],
      ['Other Cost Per Tray', `$${costPerTrayOther}`],
      ['Total Cost Per Tray', `$${costPerTrayTotal}`],
      [
        `Trays Needed For ${chargePricePerAmount} ${upperCaseFirst(chargePricePerAmountUnit)}`,
        traysNeededForSellingAmountWithBuffer,
      ],
      [
        `Cost Per ${chargePricePerAmount} ${upperCaseFirst(chargePricePerAmountUnit)}`,
        `$${costPerSellingAmountWithBuffer}`,
      ],
      ['Revenue Per Tray', `$${sellingPricePerTray || '0.00'}`],
      [
        `Revenue Per ${chargePricePerAmount} ${upperCaseFirst(chargePricePerAmountUnit)}`,
        `$${sellingPricePerSellingAmount || '0'}`,
      ],
      ['Gross Margin Per Tray', `$${marginPerTray}${marginPercentage === '' ? '' : ` (${marginPercentage}%)`}`],
      [
        `Gross Margin Per ${chargePricePerAmount} ${upperCaseFirst(chargePricePerAmountUnit)}`,
        `$${marginPerSellingAmount}${marginPercentage === '' ? '' : ` (${marginPercentage}%)`}`,
      ],
    ],
    [
      costPerTraySeed,
      costPerTraySoil,
      costPerTrayPackaging,
      costPerTrayOther,
      costPerTrayTotal,
      chargePricePerAmount,
      chargePricePerAmountUnit,
      traysNeededForSellingAmountWithBuffer,
      costPerSellingAmountWithBuffer,
      marginPerTray,
      marginPerSellingAmount,
      sellingPricePerSellingAmount,
      sellingPricePerTray,
      marginPercentage,
    ],
  );

  const results = React.useMemo(() => <TableView columns={columns} />, [columns]);

  const [monthlyWaterCost, setMonthlyWaterCost] = React.useState('');
  const [monthlyElectricityCost, setMonthlyElectricityCost] = React.useState('');
  const [monthlyLaborCost, setMonthlyLaborCost] = React.useState('');
  const [monthlyRentCost, setMonthlyRentCost] = React.useState('');
  const [monthlyPackagingCost, setMonthlyPackagingCost] = React.useState('');
  const [monthlyOtherCost, setMonthlyOtherCost] = React.useState('');
  const [hourlyLaborRate, setHourlyLaborRate] = React.useState('');
  const [laborHoursPerWeek, setLaborHoursPerWeek] = React.useState('');
  const [monthlyAmortizedEquipmentCost, setMonthlyAmortizedEquipmentCost] = React.useState('');

  const [monthlyWaterCostValidation, setMonthlyWaterCostValidation] = React.useState('');
  const [monthlyElectricityCostValidation, setMonthlyElectricityCostValidation] = React.useState('');
  const [monthlyLaborCostValidation, setMonthlyLaborCostValidation] = React.useState('');
  const [monthlyRentCostValidation, setMonthlyRentCostValidation] = React.useState('');
  const [monthlyPackagingCostValidation, setMonthlyPackagingCostValidation] = React.useState('');
  const [monthlyOtherCostValidation, setMonthlyOtherCostValidation] = React.useState('');
  const [hourlyLaborRateValidation, setHourlyLaborRateValidation] = React.useState('');
  const [laborHoursPerWeekValidation, setLaborHoursPerWeekValidation] = React.useState('');
  const [monthlyAmortizedEquipmentCostValidation, setMonthlyAmortizedEquipmentCostValidation] = React.useState('');

  const onChangeAmortizedEquipmentCost = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      validateNumInput({
        event: e,
        setValidationMessage: (msg) => setMonthlyAmortizedEquipmentCostValidation(msg),
        validateGTE: 0,
      });
      setMonthlyAmortizedEquipmentCost(e.target.value);
    },
    [],
  );
  const onAmortizedEquipmentCostBlur = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      validateNumInput({
        event: e,
        setValidationMessage: (msg) => setMonthlyAmortizedEquipmentCostValidation(msg),
        validateGTE: 0,
      });
    },
    [],
  );
  const onChangeHourlyLaborRate = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setHourlyLaborRateValidation(msg),
      validateGTE: 0,
    });
    setHourlyLaborRate(e.target.value);
  }, []);
  const onHourlyLaborRateBlur = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setHourlyLaborRateValidation(msg),
      validateGTE: 0,
    });
  }, []);
  const onChangeLaborHoursPerWeek = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      validateNumInput({
        event: e,
        setValidationMessage: (msg) => setLaborHoursPerWeekValidation(msg),
        validateGTE: 0,
      });
      setLaborHoursPerWeek(e.target.value);
    },
    [],
  );
  const onLaborHoursPerWeekBlur = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setLaborHoursPerWeekValidation(msg),
      validateGTE: 0,
    });
  }, []);
  const onChangeMonthlyWaterCost = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setMonthlyWaterCostValidation(msg),
      validateGTE: 0,
    });
    setMonthlyWaterCost(e.target.value);
  }, []);
  const onMonthlyWaterCostBlur = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setMonthlyWaterCostValidation(msg),
      validateGTE: 0,
    });
  }, []);
  const onChangeMonthlyElectricityCost = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      validateNumInput({
        event: e,
        setValidationMessage: (msg) => setMonthlyElectricityCostValidation(msg),
        validateGTE: 0,
      });
      setMonthlyElectricityCost(e.target.value);
    },
    [],
  );
  const onMonthlyElectricityCostBlur = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      validateNumInput({
        event: e,
        setValidationMessage: (msg) => setMonthlyElectricityCostValidation(msg),
        validateGTE: 0,
      });
    },
    [],
  );
  const onChangeMonthlyLaborCost = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setMonthlyLaborCostValidation(msg),
      validateGTE: 0,
    });
    setMonthlyLaborCost(e.target.value);
  }, []);
  const onMonthlyLaborCostBlur = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setMonthlyLaborCostValidation(msg),
      validateGTE: 0,
    });
  }, []);
  const onChangeMonthlyRentCost = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setMonthlyRentCostValidation(msg),
      validateGTE: 0,
    });
    setMonthlyRentCost(e.target.value);
  }, []);
  const onMonthlyRentCostBlur = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setMonthlyRentCostValidation(msg),
      validateGTE: 0,
    });
  }, []);
  const onChangeMonthlyPackagingCost = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      validateNumInput({
        event: e,
        setValidationMessage: (msg) => setMonthlyPackagingCostValidation(msg),
        validateGTE: 0,
      });
      setMonthlyPackagingCost(e.target.value);
    },
    [],
  );
  const onMonthlyPackagingCostBlur = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      validateNumInput({
        event: e,
        setValidationMessage: (msg) => setMonthlyPackagingCostValidation(msg),
        validateGTE: 0,
      });
    },
    [],
  );
  const onChangeMonthlyOtherCost = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setMonthlyOtherCostValidation(msg),
      validateGTE: 0,
    });
    setMonthlyOtherCost(e.target.value);
  }, []);
  const onMonthlyOtherCostBlur = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setMonthlyOtherCostValidation(msg),
      validateGTE: 0,
    });
  }, []);

  const fixedCostsForm = React.useMemo(
    () => (
      <Box display="flex" flexDirection="column" sx={{ m: 1 }} gap={2}>
        <TextField
          aria-describedby="water-cost"
          id="water-cost"
          label="Monthly Water Expense"
          value={monthlyWaterCost}
          onChange={onChangeMonthlyWaterCost}
          onBlur={onMonthlyWaterCostBlur}
          subtext={monthlyWaterCostValidation}
          error={monthlyWaterCostValidation !== ''}
          type="number"
          inputMode="decimal"
          numberMin={0}
          hasSubtext
        />
        <TextField
          aria-describedby="electricity-cost"
          id="electricity-cost"
          label="Monthly Electricity Expense"
          value={monthlyElectricityCost}
          onChange={onChangeMonthlyElectricityCost}
          onBlur={onMonthlyElectricityCostBlur}
          subtext={monthlyElectricityCostValidation}
          error={monthlyElectricityCostValidation !== ''}
          type="number"
          inputMode="decimal"
          numberMin={0}
          hasSubtext
        />
        <TextField
          aria-describedby="labor-cost"
          id="labor-cost"
          label="Monthly Salary Expense"
          value={monthlyLaborCost}
          onChange={onChangeMonthlyLaborCost}
          onBlur={onMonthlyLaborCostBlur}
          subtext={monthlyLaborCostValidation}
          error={monthlyLaborCostValidation !== ''}
          type="number"
          inputMode="decimal"
          numberMin={0}
          hasSubtext
        />
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              aria-describedby="hourly-labor-rate"
              id="hourly-labor-rate"
              label="Hourly Labor Rate ($/hr)"
              value={hourlyLaborRate}
              onChange={onChangeHourlyLaborRate}
              onBlur={onHourlyLaborRateBlur}
              subtext={hourlyLaborRateValidation}
              error={hourlyLaborRateValidation !== ''}
              type="number"
              inputMode="decimal"
              numberMin={0}
              hasSubtext
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              aria-describedby="labor-hours-per-week"
              id="labor-hours-per-week"
              label="Labor Hours Per Week"
              value={laborHoursPerWeek}
              onChange={onChangeLaborHoursPerWeek}
              onBlur={onLaborHoursPerWeekBlur}
              subtext={laborHoursPerWeekValidation}
              error={laborHoursPerWeekValidation !== ''}
              type="number"
              inputMode="decimal"
              numberMin={0}
              hasSubtext
            />
          </Grid>
        </Grid>
        <TextField
          aria-describedby="rent-cost"
          id="rent-cost"
          label="Monthly Rent Expense"
          value={monthlyRentCost}
          onChange={onChangeMonthlyRentCost}
          onBlur={onMonthlyRentCostBlur}
          subtext={monthlyRentCostValidation}
          error={monthlyRentCostValidation !== ''}
          type="number"
          inputMode="decimal"
          numberMin={0}
          hasSubtext
        />
        <TextField
          aria-describedby="packaging-cost"
          id="packaging-cost"
          label="Monthly Packaging and Labeling Expenses"
          value={monthlyPackagingCost}
          onChange={onChangeMonthlyPackagingCost}
          onBlur={onMonthlyPackagingCostBlur}
          subtext={monthlyPackagingCostValidation}
          error={monthlyPackagingCostValidation !== ''}
          type="number"
          inputMode="decimal"
          numberMin={0}
          hasSubtext
        />
        <TextField
          aria-describedby="amortized-equipment-cost"
          id="amortized-equipment-cost"
          label="Monthly Amortized Equipment Expenses"
          value={monthlyAmortizedEquipmentCost}
          onChange={onChangeAmortizedEquipmentCost}
          onBlur={onAmortizedEquipmentCostBlur}
          subtext={monthlyAmortizedEquipmentCostValidation}
          error={monthlyAmortizedEquipmentCostValidation !== ''}
          type="number"
          inputMode="decimal"
          numberMin={0}
          hasSubtext
        />
        <TextField
          aria-describedby="other-cost"
          id="other-cost"
          label="Other Monthly Expenses"
          value={monthlyOtherCost}
          onChange={onChangeMonthlyOtherCost}
          onBlur={onMonthlyOtherCostBlur}
          subtext={monthlyOtherCostValidation}
          error={monthlyOtherCostValidation !== ''}
          type="number"
          inputMode="decimal"
          numberMin={0}
          hasSubtext
        />
      </Box>
    ),
    [
      hourlyLaborRate,
      laborHoursPerWeek,
      monthlyElectricityCost,
      monthlyRentCost,
      onMonthlyElectricityCostBlur,
      onMonthlyWaterCostBlur,
      onMonthlyRentCostBlur,
      onMonthlyPackagingCostBlur,
      onMonthlyOtherCostBlur,
      onLaborHoursPerWeekBlur,
      onMonthlyLaborCostBlur,
      onHourlyLaborRateBlur,
      onChangeMonthlyWaterCost,
      onChangeMonthlyRentCost,
      onChangeMonthlyPackagingCost,
      onChangeMonthlyOtherCost,
      onChangeMonthlyLaborCost,
      onChangeMonthlyElectricityCost,
      onChangeLaborHoursPerWeek,
      onChangeHourlyLaborRate,
      onChangeAmortizedEquipmentCost,
      onAmortizedEquipmentCostBlur,
      monthlyWaterCostValidation,
      monthlyWaterCost,
      monthlyPackagingCostValidation,
      monthlyRentCostValidation,
      monthlyPackagingCost,
      monthlyOtherCostValidation,
      monthlyOtherCost,
      monthlyLaborCostValidation,
      monthlyLaborCost,
      monthlyAmortizedEquipmentCostValidation,
      monthlyElectricityCostValidation,
      monthlyAmortizedEquipmentCost,
      hourlyLaborRateValidation,
      laborHoursPerWeekValidation,
    ],
  );

  const [profitInputType, setProfitInputType] = React.useState<'tray-count' | 'net-profit' | 'product-amount'>(
    'net-profit',
  );
  const [netProfitAmount, setNetProfitAmount] = React.useState('');
  const [traysSoldAmount, setTraysSoldAmount] = React.useState('');
  const [productSalesAmount, setProductSalesAmount] = React.useState('');

  const [netProfitAmountValidation, setNetProfitAmountValidation] = React.useState('');
  const [traysSoldAmountValidation, setTraysSoldAmountValidation] = React.useState('');
  const [productSalesAmountValidation, setProductSalesAmountValidation] = React.useState('');

  const onChangeNetProfitAmount = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setNetProfitAmountValidation(msg),
      validateGTE: 0,
    });
    setNetProfitAmount(e.target.value);
  }, []);
  const onNetProfitAmountBlur = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setNetProfitAmountValidation(msg),
      validateGTE: 0,
    });
  }, []);
  const onChangeTraysSoldAmount = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setTraysSoldAmountValidation(msg),
      validateGTE: 0,
    });
    setTraysSoldAmount(e.target.value);
  }, []);
  const onTraysSoldAmountBlur = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setTraysSoldAmountValidation(msg),
      validateGTE: 0,
    });
  }, []);
  const onChangeProductSalesAmount = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      validateNumInput({
        event: e,
        setValidationMessage: (msg) => setProductSalesAmountValidation(msg),
        validateGTE: 0,
      });
      setProductSalesAmount(e.target.value);
    },
    [],
  );
  const onProductSalesAmountBlur = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setProductSalesAmountValidation(msg),
      validateGTE: 0,
    });
  }, []);
  const onChangeProfitInputType = React.useCallback((e: React.MouseEvent<HTMLElement>, newValue: string | null) => {
    if (newValue == null) {
      return;
    }
    setProfitInputType(newValue as 'tray-count' | 'net-profit' | 'product-amount');
  }, []);

  const profitForm = React.useMemo(
    () => (
      <Box display="flex" flexDirection="column" sx={{ m: 1 }} gap={2}>
        <Grid sx={{ mt: 1 }} container alignItems="center" justifyContent="flex-start" spacing={1}>
          <Grid>
            <Typography variant="body1">Enter One:</Typography>
          </Grid>
          <Grid>
            <ToggleButtonGroup
              size="small"
              value={profitInputType}
              exclusive
              onChange={onChangeProfitInputType}
              aria-label="select-profit-type"
            >
              <ToggleButton value="net-profit" aria-label="net profit margin">
                Net Profit Target
              </ToggleButton>
              <ToggleButton value="tray-count" aria-label="tray count sold">
                Tray Sales Target
              </ToggleButton>
              <ToggleButton value="product-amount" aria-label="monthly sales quantity">
                Container Sales Target
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        </Grid>
        {profitInputType === 'net-profit' && (
          <TextField
            aria-describedby="net-profit-amount"
            id="net-profit-amount"
            label="Monthly Net Profit Target"
            value={netProfitAmount}
            onChange={onChangeNetProfitAmount}
            onBlur={onNetProfitAmountBlur}
            subtext={netProfitAmountValidation}
            error={netProfitAmountValidation !== ''}
            type="number"
            inputMode="decimal"
            numberMin={0}
            hasSubtext
          />
        )}
        {profitInputType === 'tray-count' && (
          <TextField
            aria-describedby="tray-count"
            id="tray-count"
            label="Monthly Trays Sales Target"
            value={traysSoldAmount}
            onChange={onChangeTraysSoldAmount}
            onBlur={onTraysSoldAmountBlur}
            subtext={traysSoldAmountValidation}
            error={traysSoldAmountValidation !== ''}
            type="number"
            inputMode="decimal"
            numberMin={0}
            hasSubtext
          />
        )}
        {profitInputType === 'product-amount' && (
          <TextField
            aria-describedby="product-sales-amount"
            id="product-sales-amount"
            label="Monthly Container Sales Target"
            value={productSalesAmount}
            onChange={onChangeProductSalesAmount}
            onBlur={onProductSalesAmountBlur}
            subtext={productSalesAmountValidation}
            error={productSalesAmountValidation !== ''}
            type="number"
            inputMode="decimal"
            numberMin={0}
            hasSubtext
          />
        )}
      </Box>
    ),
    [
      netProfitAmount,
      onChangeNetProfitAmount,
      onNetProfitAmountBlur,
      traysSoldAmountValidation,
      traysSoldAmount,
      profitInputType,
      productSalesAmountValidation,
      productSalesAmount,
      onTraysSoldAmountBlur,
      onChangeTraysSoldAmount,
      onProductSalesAmountBlur,
      onChangeProfitInputType,
      netProfitAmountValidation,
      onChangeProductSalesAmount,
    ],
  );

  interface ProfitCalculation {
    readonly netProfitAmountOut: Decimal;
    readonly netProfitPercentageOut: Decimal;
    readonly grossMarginNeeded: Decimal;
    readonly grossMarginPercentageOut: Decimal;
    readonly sellingAmountNeeded: Decimal;
    readonly sellingTraysNeeded: Decimal;
    readonly traysNeededToGrow: Decimal;
    readonly revenueNeeded: Decimal;
    readonly costOfGoodsSold: Decimal;
  }
  const getProfitCalculations = React.useCallback(() => {
    const monthlyWaterCostNum = Number.parseFloat(monthlyWaterCost) || 0;
    const monthlyElectricityCostNum = Number.parseFloat(monthlyElectricityCost) || 0;
    const monthlyLaborCostNum = Number.parseFloat(monthlyLaborCost) || 0;
    const monthlyRentCostNum = Number.parseFloat(monthlyRentCost) || 0;
    const monthlyPackagingCostNum = Number.parseFloat(monthlyPackagingCost) || 0;
    const monthlyOtherCostNum = Number.parseFloat(monthlyOtherCost) || 0;
    const hourlyLaborRateNum = Number.parseFloat(hourlyLaborRate) || 0;
    const laborHoursPerWeekNum = Number.parseFloat(laborHoursPerWeek) || 0;
    const netProfitAmountNum = Number.parseFloat(netProfitAmount) || 0;
    const productSalesAmountNum = Number.parseFloat(productSalesAmount) || 0;
    const traysSoldNum = Number.parseFloat(traysSoldAmount) || 0;
    const monthlyAmortizedEquipmentCostNum = Number.parseFloat(monthlyAmortizedEquipmentCost) || 0;

    const monthlyHourLaborCost = Decimal.mul(hourlyLaborRateNum, laborHoursPerWeekNum).mul(4.34524).toNumber();
    const totalMonthlyFixedCosts = [
      monthlyWaterCostNum,
      monthlyElectricityCostNum,
      monthlyLaborCostNum,
      monthlyRentCostNum,
      monthlyPackagingCostNum,
      monthlyOtherCostNum,
      monthlyHourLaborCost,
      monthlyAmortizedEquipmentCostNum,
    ].reduce((acc, val) => acc.add(val), new Decimal(0));

    // Get values from net profit amount
    const useNetProfitAmount = (): ProfitCalculation => {
      const netProfitAmountOut = new Decimal(netProfitAmountNum);
      const grossMarginNeeded = netProfitAmountOut.add(totalMonthlyFixedCosts);
      const sellingAmountNeeded = grossMarginNeeded.div(marginPerSellingAmountDecimal);
      const sellingTraysNeeded = sellingAmountNeeded.mul(traysNeededForSellingAmountWithoutBufferDecimal);
      const traysNeededToGrow = sellingTraysNeeded.add(sellingTraysNeeded.mul(trayBufferPercentageDecimal));
      // For COGS use selling amount since that's what we're using to get revenue here
      const costOfGoodsSold = sellingAmountNeeded.mul(costPerSellingAmountWithBufferDecimal);
      const revenueNeeded = grossMarginNeeded.add(costOfGoodsSold);

      const netProfitPercentageOut = netProfitAmountOut.div(revenueNeeded).mul(100);
      const grossMarginPercentageOut = grossMarginNeeded.div(revenueNeeded).mul(100);

      return {
        netProfitAmountOut,
        netProfitPercentageOut,
        grossMarginNeeded,
        grossMarginPercentageOut,
        sellingAmountNeeded,
        sellingTraysNeeded,
        traysNeededToGrow,
        revenueNeeded,
        costOfGoodsSold,
      };
    };
    // Get values from product sales amount
    const useProductSalesAmount = (): ProfitCalculation => {
      const sellingAmountNeeded = new Decimal(productSalesAmountNum);
      const costOfGoodsSold = sellingAmountNeeded.mul(costPerSellingAmountWithBufferDecimal);
      // This is the number of trays being sold, not the number of trays that will be grown
      const sellingTraysNeeded = sellingAmountNeeded.mul(traysNeededForSellingAmountWithoutBufferDecimal);
      const traysNeededToGrow = sellingTraysNeeded.add(sellingTraysNeeded.mul(trayBufferPercentageDecimal));
      const revenueNeeded = sellingAmountNeeded.mul(sellingPricePerSellingAmountDecimal);
      const grossMarginNeeded = revenueNeeded.sub(costOfGoodsSold);
      const netProfitAmountOut = grossMarginNeeded.sub(totalMonthlyFixedCosts);

      const netProfitPercentageOut = netProfitAmountOut.div(revenueNeeded).mul(100);
      const grossMarginPercentageOut = grossMarginNeeded.div(revenueNeeded).mul(100);

      return {
        netProfitAmountOut,
        netProfitPercentageOut,
        grossMarginNeeded,
        grossMarginPercentageOut,
        sellingAmountNeeded,
        sellingTraysNeeded,
        traysNeededToGrow,
        revenueNeeded,
        costOfGoodsSold,
      };
    };
    // Get values from trays sold
    const useTraysSold = (): ProfitCalculation => {
      const sellingTraysNeeded = new Decimal(traysSoldNum);
      const sellingAmountNeeded = sellingTraysNeeded.div(traysNeededForSellingAmountWithoutBufferDecimal);
      // Trays sold is not the same as trays grown. Trays grown could be larger than trays sold if there is a buffer
      const traysNeededToGrow = sellingTraysNeeded.add(sellingTraysNeeded.mul(trayBufferPercentageDecimal));
      const costOfGoodsSold = traysNeededToGrow.mul(costPerTrayTotalDecimal);
      const revenueNeeded = sellingTraysNeeded.mul(sellingPricePerTrayDecimal);
      const grossMarginNeeded = revenueNeeded.sub(costOfGoodsSold);
      const netProfitAmountOut = grossMarginNeeded.sub(totalMonthlyFixedCosts);

      const netProfitPercentageOut = netProfitAmountOut.div(revenueNeeded).mul(100);
      const grossMarginPercentageOut = grossMarginNeeded.div(revenueNeeded).mul(100);

      return {
        netProfitAmountOut,
        netProfitPercentageOut,
        grossMarginNeeded,
        grossMarginPercentageOut,
        sellingAmountNeeded,
        sellingTraysNeeded,
        traysNeededToGrow,
        revenueNeeded,
        costOfGoodsSold,
      };
    };
    const result =
      profitInputType === 'net-profit'
        ? useNetProfitAmount()
        : profitInputType === 'tray-count'
          ? useTraysSold()
          : useProductSalesAmount();
    const monthlyCostOfGoodsSold = result.costOfGoodsSold;
    const sellingTraysNeededIsInvalid =
      result.sellingTraysNeeded.isNaN() ||
      result.sellingTraysNeeded.lessThan(0) ||
      !result.sellingTraysNeeded.isFinite();
    const traysNeededToGrowIsInvalid =
      result.traysNeededToGrow.isNaN() || result.traysNeededToGrow.lessThan(0) || !result.traysNeededToGrow.isFinite();

    return {
      totalMonthlyFixedCosts: formatDecimalToCurrency(totalMonthlyFixedCosts),
      monthlyCostOfGoodsSold:
        monthlyCostOfGoodsSold.isNaN() || monthlyCostOfGoodsSold.lessThan(0) || !monthlyCostOfGoodsSold.isFinite()
          ? ''
          : formatDecimalToCurrency(monthlyCostOfGoodsSold),
      sellingTraysNeeded: sellingTraysNeededIsInvalid ? '-' : result.sellingTraysNeeded.toFixed(2),
      growingTraysNeeded: traysNeededToGrowIsInvalid ? '-' : result.traysNeededToGrow.toFixed(2),
      sellingAmountNeeded:
        result.sellingAmountNeeded.isNaN() ||
        result.sellingAmountNeeded.lessThan(0) ||
        !result.sellingAmountNeeded.isFinite()
          ? '-'
          : result.sellingAmountNeeded.toFixed(2),
      grossMarginNeeded: formatDecimalToCurrency(result.grossMarginNeeded),
      grossMarginPercentageOut:
        result.grossMarginPercentageOut.isNaN() ||
        result.grossMarginPercentageOut.lessThan(0) ||
        !result.grossMarginPercentageOut.isFinite()
          ? ''
          : result.grossMarginPercentageOut.toFixed(0),
      revenueNeeded:
        result.revenueNeeded.isNaN() || result.revenueNeeded.lessThan(0) || !result.revenueNeeded.isFinite()
          ? ''
          : formatDecimalToCurrency(result.revenueNeeded),
      revenueNeededDecimal: result.revenueNeeded,
      netProfitAmountOut: formatDecimalToCurrency(result.netProfitAmountOut),
      annualNetProfitAmountOut: formatDecimalToCurrency(result.netProfitAmountOut.mul(12)),
      annualNetProfitAmountOutDecimal: result.netProfitAmountOut.mul(12),
      netProfitPercentageOut:
        result.netProfitPercentageOut.isNaN() ||
        result.netProfitPercentageOut.lessThan(0) ||
        !result.netProfitPercentageOut.isFinite()
          ? ''
          : result.netProfitPercentageOut.toFixed(0),
    };
  }, [
    costPerSellingAmountWithBufferDecimal,
    hourlyLaborRate,
    monthlyElectricityCost,
    profitInputType,
    traysSoldAmount,
    traysNeededForSellingAmountWithoutBufferDecimal,
    trayBufferPercentageDecimal,
    sellingPricePerTrayDecimal,
    productSalesAmount,
    sellingPricePerSellingAmountDecimal,
    netProfitAmount,
    monthlyWaterCost,
    monthlyRentCost,
    monthlyPackagingCost,
    monthlyOtherCost,
    monthlyAmortizedEquipmentCost,
    monthlyLaborCost,
    marginPerSellingAmountDecimal,
    costPerTrayTotalDecimal,
    laborHoursPerWeek,
  ]);

  const {
    monthlyCostOfGoodsSold,
    totalMonthlyFixedCosts,
    grossMarginNeeded,
    grossMarginPercentageOut,
    sellingTraysNeeded,
    growingTraysNeeded,
    sellingAmountNeeded,
    revenueNeeded,
    revenueNeededDecimal,
    netProfitAmountOut,
    netProfitPercentageOut,
    annualNetProfitAmountOut,
    annualNetProfitAmountOutDecimal,
  } = React.useMemo(() => getProfitCalculations(), [getProfitCalculations]);

  const profitColumns = React.useMemo(
    (): Array<[string, React.ReactElement | string] | [string, React.ReactElement | string, number]> => [
      ['Monthly Trays Grown', `${growingTraysNeeded}`],
      ['Monthly Tray Sales', `${sellingTraysNeeded}`],
      ['Monthly Container Sales', `${sellingAmountNeeded}`],
      ['Monthly Revenue', `${revenueNeeded === '' ? '-' : `$${revenueNeeded}`}`],
      ['Cost of Goods Sold', `${monthlyCostOfGoodsSold === '' ? '-' : `$${monthlyCostOfGoodsSold}`}`],
      [
        'Gross Margin',
        `$${grossMarginNeeded} ${grossMarginPercentageOut === '' ? '' : `(${grossMarginPercentageOut}%)`}`,
      ],
      ['Fixed Costs', `$${totalMonthlyFixedCosts}`],
      ['Net Profit', `$${netProfitAmountOut} ${netProfitPercentageOut === '' ? '' : `(${netProfitPercentageOut}%)`}`],
      [
        'Annual Net Profit',
        `$${annualNetProfitAmountOut} ${netProfitPercentageOut === '' ? '' : `(${netProfitPercentageOut}%)`}`,
      ],
    ],
    [
      annualNetProfitAmountOut,
      grossMarginPercentageOut,
      netProfitPercentageOut,
      totalMonthlyFixedCosts,
      sellingTraysNeeded,
      sellingAmountNeeded,
      netProfitAmountOut,
      revenueNeeded,
      monthlyCostOfGoodsSold,
      grossMarginNeeded,
      growingTraysNeeded,
    ],
  );

  const profitResults = React.useMemo(() => <TableView columns={profitColumns} />, [profitColumns]);

  const [taxRate, setTaxRate] = React.useState('');
  const [taxRateValidation, setTaxRateValidation] = React.useState('');

  const onChangeTaxRate = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setTaxRateValidation(msg),
      validateGTE: 0,
      validateLT: 100,
    });
    setTaxRate(e.target.value);
  }, []);
  const onTaxRateBlur = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      event: e,
      setValidationMessage: (msg) => setTaxRateValidation(msg),
      validateGTE: 0,
      validateLT: 100,
    });
  }, []);
  const taxForm = React.useMemo(
    () => (
      <Box display="flex" flexDirection="column" sx={{ m: 1 }} gap={2}>
        <TextField
          aria-describedby="tax-amount"
          id="tax-amount"
          label="Tax Rate (%)"
          value={taxRate}
          onChange={onChangeTaxRate}
          onBlur={onTaxRateBlur}
          subtext={taxRateValidation}
          error={taxRateValidation !== ''}
          type="number"
          inputMode="decimal"
          numberMin={0}
          numberMax={99}
          hasSubtext
        />
      </Box>
    ),
    [taxRate, onChangeTaxRate, onTaxRateBlur, taxRateValidation],
  );

  const taxColumns = React.useMemo(() => {
    const taxRateNum = Number.parseFloat(taxRate) || 0;
    const taxAmount = annualNetProfitAmountOutDecimal.mul(taxRateNum).div(100);
    const annualProfitAfterTax = annualNetProfitAmountOutDecimal.sub(taxAmount);
    const annualProfitAfterTaxMargin = annualProfitAfterTax.div(revenueNeededDecimal.mul(12)).mul(100);
    const afterTaxMarginString =
      annualProfitAfterTaxMargin.isNaN() ||
      annualProfitAfterTaxMargin.lessThan(0) ||
      !annualProfitAfterTaxMargin.isFinite()
        ? ''
        : ` (${annualProfitAfterTaxMargin.toFixed(0)}%)`;

    return [
      ['Annual Tax', `$${formatDecimalToCurrency(taxAmount)}`],
      ['Annual Profit After Tax', `$${formatDecimalToCurrency(annualProfitAfterTax)}${afterTaxMarginString}`],
    ] as Array<[string, React.ReactElement | string] | [string, React.ReactElement | string, number]>;
  }, [annualNetProfitAmountOutDecimal, taxRate, revenueNeededDecimal]);

  const taxResults = React.useMemo(() => <TableView columns={taxColumns} />, [taxColumns]);

  const onClear = React.useCallback(() => {
    setSowDensity('');
    setSowDensityValidation('');
    setYieldPerTray('');
    setYieldPerTrayValidation('');
    setSeedCost('');
    setSeedCostValidation('');
    setSeedCostPerAmount('1');
    setChargePrice('');
    setChargePriceValidation('');
    setChargePricePerAmount('1');
    setChargePricePerAmountValidation('');
    setOtherCostPerTray('');
    setOtherCostPerTrayValidation('');
    setSoilCost('');
    setSoilCostValidation('');
    setTraysPerSoilAmount('');
    setTraysPerSoilAmountValidation('');
    setTargetGrossMarginPercentage('');
    setTargetGrossMarginValidation('');
    setPackagingLabelingCostPerTray('');
    setPackagingLabelingCostPerTrayValidation('');
    setMonthlyAmortizedEquipmentCost('');
    setUseProductPrice(true);

    setMonthlyWaterCost('');
    setMonthlyElectricityCost('');
    setMonthlyLaborCost('');
    setMonthlyRentCost('');
    setMonthlyPackagingCost('');
    setMonthlyOtherCost('');
    setMonthlyWaterCostValidation('');
    setMonthlyElectricityCostValidation('');
    setMonthlyLaborCostValidation('');
    setMonthlyRentCostValidation('');
    setMonthlyPackagingCostValidation('');
    setMonthlyOtherCostValidation('');
    setMonthlyAmortizedEquipmentCostValidation('');

    setProfitInputType('net-profit');
    setNetProfitAmount('');
    setTraysSoldAmount('');
    setProductSalesAmount('');
    setHourlyLaborRate('');
    setLaborHoursPerWeek('');

    setNetProfitAmountValidation('');
    setTraysSoldAmountValidation('');
    setProductSalesAmountValidation('');
    setHourlyLaborRateValidation('');
    setLaborHoursPerWeekValidation('');

    setTaxRate('');
    setTaxRateValidation('');

    setLink('');
  }, []);
  const useDefaults = React.useCallback(() => {
    onClear();

    setSeedCost(defaultValues.seedCost);
    setSeedCostPerAmount(defaultValues.seedCostPerAmount);
    setSeedCostPerAmountUnit(defaultValues.seedCostPerAmountUnit);
    setSowDensity(defaultValues.sowDensity);
    setSowDensityUnit(defaultValues.sowDensityUnit);
    setSoilCost(defaultValues.soilCostPerBag);
    setTraysPerSoilAmount(defaultValues.traysPerSoilAmount);
    setPackagingLabelingCostPerTray(defaultValues.packagingLabelingCostPerTray);
    setOtherCostPerTray(defaultValues.otherCostPerTray);
    setYieldPerTray(defaultValues.yieldPerTray);
    setYieldUnit(defaultValues.yieldPerTrayUnit);
    setChargePrice(defaultValues.chargePrice);
    setChargePricePerAmount(defaultValues.chargePricePerAmount);
    setChargePricePerAmountUnit(defaultValues.chargePricePerAmountUnit);
    setTargetGrossMarginPercentage(defaultValues.grossMarginPercentage);
    setMonthlyWaterCost(defaultValues.monthlyWaterCost);
    setMonthlyElectricityCost(defaultValues.monthlyElectricityCost);
    setMonthlyLaborCost(defaultValues.monthlyLaborCost);
    setHourlyLaborRate(defaultValues.hourlyLaborRate);
    setLaborHoursPerWeek(defaultValues.laborHoursPerWeek);
    setMonthlyRentCost(defaultValues.monthlyRentCost);
    setMonthlyPackagingCost(defaultValues.monthlyPackagingCost);
    setMonthlyAmortizedEquipmentCost(defaultValues.monthlyAmortizedEquipmentCost);
    setMonthlyOtherCost(defaultValues.monthlyOtherCost);
    setNetProfitAmount(defaultValues.monthlyNetProfitAmountTarget);
    setTraysSoldAmount(defaultValues.monthlyTraySalesTarget);
    setProductSalesAmount(defaultValues.monthlyContainerSalesTarget);
    setTaxRate(defaultValues.taxRate);
    setTrayBuffer(defaultValues.trayBuffer);
  }, [onClear]);

  const [link, setLink] = React.useState('');

  const onSaveLink = React.useCallback(() => {
    // Create URL encoded link
    const params = new URLSearchParams();
    const values: readonly [string, string][] = [
      // 30 total fields
      [PARAMS.seedCost, seedCost],
      [PARAMS.seedCostAmt, seedCostPerAmount],
      [PARAMS.seedCostUnit, seedCostPerAmountUnit],
      [PARAMS.sowDensity, sowDensity],
      [PARAMS.sowDensityUnit, sowDensityUnit],
      [PARAMS.soilCost, soilCost],
      [PARAMS.traysPerSoil, traysPerSoilAmount],
      [PARAMS.packagingPerTray, packagingLabelingCostPerTray],
      [PARAMS.packagingPerUnit, packagingLabelingCostPerTrayUnit],
      [PARAMS.otherPerTray, otherCostPerTray],
      [PARAMS.yieldPerTray, yieldPerTray],
      [PARAMS.yieldUnit, yieldUnit],
      [PARAMS.useProductPrice, useProductPrice ? 'true' : 'false'],
      [PARAMS.grossMargin, targetGrossMarginPercentage],
      [PARAMS.chargePrice, chargePrice],
      [PARAMS.chargePriceAmt, chargePricePerAmount],
      [PARAMS.chargePriceUnit, chargePricePerAmountUnit],
      [PARAMS.monthlyWater, monthlyWaterCost],
      [PARAMS.monthlyElectricity, monthlyElectricityCost],
      [PARAMS.monthlyLabor, monthlyLaborCost],
      [PARAMS.hourlyLabor, hourlyLaborRate],
      [PARAMS.laborHoursPerWeek, laborHoursPerWeek],
      [PARAMS.monthlyRent, monthlyRentCost],
      [PARAMS.monthlyPackaging, monthlyPackagingCost],
      [PARAMS.monthlyAmortization, monthlyAmortizedEquipmentCost],
      [PARAMS.monthlyOther, monthlyOtherCost],
      [PARAMS.targetOption, profitInputType],
      [PARAMS.netProfitTarget, netProfitAmount],
      [PARAMS.trayTarget, traysSoldAmount],
      [PARAMS.productTarget, productSalesAmount],
      [PARAMS.taxRate, taxRate],
      [PARAMS.trayBuff, trayBuffer],
    ];
    for (const [key, value] of values) {
      if (value) {
        params.append(key, value);
      }
    }
    const fullUrl = `${window.location.origin}${routes.PRICE_CALCULATOR}${
      params.size > 0 ? '?' : ''
    }${params.toString()}`;

    setLink(fullUrl);
  }, [
    chargePrice,
    chargePricePerAmountUnit,
    monthlyElectricityCost,
    packagingLabelingCostPerTray,
    yieldPerTray,
    useProductPrice,
    yieldUnit,
    traysSoldAmount,
    traysPerSoilAmount,
    trayBuffer,
    taxRate,
    targetGrossMarginPercentage,
    sowDensityUnit,
    sowDensity,
    soilCost,
    seedCostPerAmountUnit,
    seedCostPerAmount,
    seedCost,
    profitInputType,
    productSalesAmount,
    otherCostPerTray,
    packagingLabelingCostPerTrayUnit,
    netProfitAmount,
    monthlyWaterCost,
    monthlyRentCost,
    monthlyPackagingCost,
    monthlyOtherCost,
    monthlyAmortizedEquipmentCost,
    monthlyLaborCost,
    laborHoursPerWeek,
    chargePricePerAmount,
    hourlyLaborRate,
  ]);

  return (
    <CenteredView>
      <Helmet>
        <title>Microgreen Pricing and Profit Calculator</title>
        <meta
          name="description"
          content={
            'Free microgreen pricing and profit calculator tool to easily calculate your costs per tray, and your gross profit margin per tray. Figure out how much to charge for your microgreens or how much it will cost to grow them. You can then also enter fixed monthly expenses and see how much you need to grow and sell to make a profit.'
          }
        />
      </Helmet>
      <Container disableGutters component="div" maxWidth="md">
        <Card
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: 2,
          }}
        >
          <>
            <Typography sx={{ p: 1, textAlign: 'center' }} variant="h4" component="h2">
              Microgreen Pricing and Profit Calculator
            </Typography>
            <Typography sx={{ p: 1, textAlign: 'center' }} variant="body1" component="p">
              To learn more about how to use the calculator, check out our{' '}
              <Link
                inlineText
                path={routes.makeVideoPath(routes.PRICE_CALCULATOR_VIDEO)}
                variant="body1"
                title="video tutorial"
              />
              .
            </Typography>
            <Box sx={{ mt: 3, width: '100%' }}>
              {form}
              <Box>{results}</Box>
              {fixedCostsForm}
              {profitForm}
              <Box>{profitResults}</Box>
              {taxForm}
              <Box>{taxResults}</Box>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3 }}
                onClick={onClear}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onClear();
                  }
                }}
              >
                Clear All
              </Button>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 1 }}
                onClick={useDefaults}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    useDefaults();
                  }
                }}
              >
                Populate Defaults
              </Button>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 1 }}
                onClick={onSaveLink}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onSaveLink();
                  }
                }}
              >
                {`${link !== '' ? 'Update Link' : 'Create Link'}`}
              </Button>
              <Box
                sx={{
                  mb: 2,
                  width: '100%',
                  overflowWrap: 'break-word',
                  wordWrap: 'break-word',
                  wordBreak: 'break-word',
                }}
              >
                {link !== '' && (
                  <>
                    <Typography sx={{ mt: 2, textAlign: 'center' }} variant="body1">
                      Full link (click to copy):
                    </Typography>
                    <CopyText sx={{ textAlign: 'center' }} name="Price Calculator Link" value={link} />
                  </>
                )}
              </Box>
              {!isLoggedIn && (
                <Grid container justifyContent="center">
                  <Grid>
                    <Typography sx={{ textAlign: 'center' }} variant="body2">
                      Want more advanced planning and farm management tools like this?{' '}
                      <Link inlineText variant="body2" path={routes.SIGNUP} title="Sign up" /> for Microgreen Manager
                      and see how our software can help you manage your farm!
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </Box>
          </>
        </Card>
      </Container>
    </CenteredView>
  );
});

interface DefaultValues {
  readonly seedCost: string;
  readonly seedCostPerAmount: string;
  readonly seedCostPerAmountUnit: ConvertibleWeightUnit;
  readonly sowDensity: string;
  readonly sowDensityUnit: ConvertibleWeightUnit;
  readonly soilCostPerBag: string;
  readonly traysPerSoilAmount: string;
  readonly packagingLabelingCostPerTray: string;
  readonly packagingLabelingCostPerTrayUnit: string;
  readonly otherCostPerTray: string;
  readonly yieldPerTray: string;
  readonly yieldPerTrayUnit: ConvertibleWeightUnit;
  readonly trayBuffer: string;
  readonly chargePrice: string;
  readonly chargePricePerAmount: string;
  readonly chargePricePerAmountUnit: ConvertibleWeightUnit;
  readonly grossMarginPercentage: string;
  readonly monthlyWaterCost: string;
  readonly monthlyElectricityCost: string;
  readonly monthlyLaborCost: string;
  readonly hourlyLaborRate: string;
  readonly laborHoursPerWeek: string;
  readonly monthlyRentCost: string;
  readonly monthlyPackagingCost: string;
  readonly monthlyAmortizedEquipmentCost: string;
  readonly monthlyOtherCost: string;
  readonly monthlyNetProfitAmountTarget: string;
  readonly monthlyTraySalesTarget: string;
  readonly monthlyContainerSalesTarget: string;
  readonly taxRate: string;
}

const defaultValues: DefaultValues = {
  seedCost: '10',
  seedCostPerAmount: '1',
  seedCostPerAmountUnit: CONVERTIBLE_WEIGHT_UNITS.POUNDS,
  sowDensity: '40',
  sowDensityUnit: CONVERTIBLE_WEIGHT_UNITS.GRAMS,
  soilCostPerBag: '56',
  traysPerSoilAmount: '70',
  packagingLabelingCostPerTray: '1',
  packagingLabelingCostPerTrayUnit: 'tray',
  otherCostPerTray: '0.25',
  yieldPerTray: '12',
  yieldPerTrayUnit: CONVERTIBLE_WEIGHT_UNITS.OUNCES,
  trayBuffer: '0',
  chargePrice: '3',
  chargePricePerAmount: '1',
  chargePricePerAmountUnit: CONVERTIBLE_WEIGHT_UNITS.OUNCES,
  grossMarginPercentage: '92',
  monthlyWaterCost: '50',
  monthlyElectricityCost: '300',
  monthlyLaborCost: '',
  hourlyLaborRate: '15',
  laborHoursPerWeek: '60',
  monthlyRentCost: '400',
  monthlyPackagingCost: '',
  monthlyAmortizedEquipmentCost: '',
  monthlyOtherCost: '15',
  monthlyNetProfitAmountTarget: '4000',
  monthlyTraySalesTarget: '251',
  monthlyContainerSalesTarget: '3132',
  taxRate: '12',
};

const PARAMS = {
  seedCost: 'seedCost',
  seedCostAmt: 'seedCostAmt',
  seedCostUnit: 'seedCostUnit',
  sowDensity: 'sowDensity',
  sowDensityUnit: 'sowDensityUnit',
  soilCost: 'soilCost',
  traysPerSoil: 'traysPerSoil',
  packagingPerTray: 'packagingPerTray',
  packagingPerUnit: 'packagingUnit',
  otherPerTray: 'otherPerTray',
  yieldPerTray: 'yieldPerTray',
  yieldUnit: 'yieldUnit',
  trayBuff: 'trayBuff',
  useProductPrice: 'useProductPrice',
  grossMargin: 'grossMargin',
  chargePrice: 'chargePrice',
  chargePriceAmt: 'chargePriceAmt',
  chargePriceUnit: 'chargePriceUnit',
  monthlyWater: 'monthlyWater',
  monthlyElectricity: 'monthlyElectricity',
  monthlyLabor: 'monthlyLabor',
  hourlyLabor: 'hourlyLabor',
  laborHoursPerWeek: 'laborHoursPerWeek',
  monthlyRent: 'monthlyRent',
  monthlyPackaging: 'monthlyPackaging',
  monthlyAmortization: 'monthlyAmortization',
  monthlyOther: 'monthlyOther',
  targetOption: 'targetOption',
  netProfitTarget: 'netProfitTarget',
  trayTarget: 'trayTarget',
  productTarget: 'productTarget',
  taxRate: 'taxRate',
};
